import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const SEVERITY_CONFIG = {
  'CRÍTICO': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500', icon: 'AlertCircle' },
  'ADVERTENCIA': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500', icon: 'AlertTriangle' },
  'INFO': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500', icon: 'Info' },
};

const TYPE_ICONS = {
  EXPOSURE_WARNING: 'TrendingUp',
  EXPOSURE_CRITICAL: 'TrendingDown',
  INVENTORY_AGE: 'Clock',
  MARGIN_BELOW_MIN: 'DollarSign',
  BENCHMARK_SHOCK: 'Zap',
  CONCENTRATION_LIMIT: 'PieChart',
};

const STATUS_TABS = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Activos' },
  { key: 'acknowledged', label: 'Reconocidos' },
  { key: 'resolved', label: 'Resueltos' },
];

export default function ActiveAlertsPanel({ alerts, loading, onAcknowledge, onResolve, onBulkAcknowledge, newAlertIds }) {
  const [activeTab, setActiveTab] = useState('active');
  const [resolveModal, setResolveModal] = useState(null); // { alertId }
  const [resolveNote, setResolveNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const filtered = (alerts || [])?.filter(a => {
    if (activeTab === 'all') return true;
    return a?.status === activeTab;
  });

  const activeAlerts = (alerts || [])?.filter(a => a?.status === 'active');

  const handleResolveSubmit = async () => {
    if (!resolveModal?.alertId) return;
    setSubmitting(true);
    await onResolve?.(resolveModal?.alertId, resolveNote);
    setSubmitting(false);
    setResolveModal(null);
    setResolveNote('');
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `hace ${diffMins}m`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `hace ${diffHrs}h`;
    return d?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={16} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground">Alertas Activas</h2>
          {activeAlerts?.length > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{activeAlerts?.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeAlerts?.length > 0 && (
            <button
              onClick={() => onBulkAcknowledge?.(activeAlerts?.map(a => a?.id))}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
            >
              <Icon name="CheckCheck" size={13} color="currentColor" />
              Reconocer todos
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-0">
        {STATUS_TABS?.map(tab => (
          <button
            key={tab?.key}
            onClick={() => setActiveTab(tab?.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors border-b-2 ${
              activeTab === tab?.key
                ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab?.label}
            {tab?.key !== 'all' && (
              <span className="ml-1.5 text-xs opacity-70">
                {(alerts || [])?.filter(a => a?.status === tab?.key)?.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Alerts list */}
      <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
        {loading ? (
          Array.from({ length: 4 })?.map((_, i) => (
            <div key={i} className="p-4 flex gap-3">
              <div className="w-8 h-8 rounded-full bg-muted animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-muted rounded animate-pulse w-48" />
                <div className="h-3 bg-muted rounded animate-pulse w-64" />
                <div className="h-3 bg-muted rounded animate-pulse w-32" />
              </div>
            </div>
          ))
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <Icon name="BellOff" size={18} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm font-medium text-foreground">Sin alertas</p>
            <p className="text-xs text-muted-foreground mt-1">No hay alertas en esta categoría</p>
          </div>
        ) : (
          filtered?.map(alert => {
            const sev = SEVERITY_CONFIG?.[alert?.severity] || SEVERITY_CONFIG?.['INFO'];
            const typeIcon = TYPE_ICONS?.[alert?.type] || 'Bell';
            const isNew = newAlertIds?.has(alert?.id);
            return (
              <div
                key={alert?.id}
                className={`p-4 flex gap-3 transition-all ${
                  isNew ? 'animate-pulse bg-accent/5' : 'hover:bg-muted/20'
                }`}
              >
                {/* Severity icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${sev?.bg}`}>
                  <Icon name={typeIcon} size={15} color={sev?.text?.replace('text-', '')} strokeWidth={2} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sev?.bg} ${sev?.text} ${sev?.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sev?.dot}`} />
                        {alert?.severity}
                      </span>
                      <span className="text-xs font-semibold text-foreground">{alert?.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 font-mono">
                      {fmtTime(alert?.triggered_at)}
                    </span>
                  </div>
                  {alert?.description && (
                    <p className="text-xs text-muted-foreground mb-1.5">{alert?.description}</p>
                  )}
                  {alert?.entity_type && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-mono">
                      <Icon name="Tag" size={10} color="currentColor" />
                      {alert?.entity_type}: {alert?.entity_id || '—'}
                    </span>
                  )}
                  {/* Actions */}
                  {alert?.status === 'active' && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => onAcknowledge?.(alert?.id)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                      >
                        <Icon name="Eye" size={11} color="currentColor" />
                        Reconocer
                      </button>
                      <button
                        onClick={() => setResolveModal({ alertId: alert?.id })}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                      >
                        <Icon name="CheckCircle" size={11} color="currentColor" />
                        Resolver
                      </button>
                    </div>
                  )}
                  {alert?.status === 'acknowledged' && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => setResolveModal({ alertId: alert?.id })}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                      >
                        <Icon name="CheckCircle" size={11} color="currentColor" />
                        Resolver
                      </button>
                    </div>
                  )}
                  {alert?.status === 'resolved' && alert?.resolution_note && (
                    <p className="text-xs text-muted-foreground mt-1 italic">Nota: {alert?.resolution_note}</p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Resolve modal */}
      {resolveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md" style={{ boxShadow: 'var(--shadow-xl)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Resolver alerta</h3>
              <button onClick={() => setResolveModal(null)} className="p-1 rounded hover:bg-muted transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Nota de resolución (opcional)</label>
            <textarea
              value={resolveNote}
              onChange={e => setResolveNote(e?.target?.value)}
              placeholder="Describe cómo se resolvió esta alerta..."
              rows={3}
              className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setResolveModal(null)}
                className="px-4 py-2 text-xs font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleResolveSubmit}
                disabled={submitting}
                className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ background: 'var(--color-accent)' }}
              >
                {submitting ? 'Guardando...' : 'Marcar como resuelto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
